import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { psychiatryAPI } from '../../utils/api';
import PatientLookup from '../../components/shared/PatientLookup';

const COUNSELLORS = ['Dr. Sana Khan', 'Dr. Ayesha Farooq', 'Dr. Nadia Ahmed', 'Dr. Usman Malik', 'Dr. Fatima Rizvi'];
const REFERRAL_SOURCES = ['Self Referral', 'OPD Doctor', 'Emergency', 'IPD Ward', 'Family/Caregiver', 'Other Hospital', 'Community'];
const BIRTH_ORDERS = ['First Born', 'Second Born', 'Middle Child', 'Youngest', 'Only Child'];
const FAMILY_STRUCTURES = ['Nuclear Family (parents + siblings)', 'Joint Family (extended)', 'Single Parent - Mother', 'Single Parent - Father', 'With Relatives', 'Guardian/Foster Care', 'Alone'];
const EDUCATION = ['Illiterate', 'Primary', 'Middle', 'Matriculation', 'Intermediate', 'Graduate', 'Post Graduate'];
const SOURCES_OF_DISTRESS = [
    'Emotional difficulties (sadness, anxiety, anger)', 'Family conflict (marital issues, separation)',
    'Academic/work pressure', 'Domestic violence or harsh discipline', 'Financial stress',
    'Grief/loss of loved one', 'Chronic illness (self or family)', 'Trauma/abuse history',
    'Relationship problems', 'Social isolation', 'Substance use', 'Occupational stress',
    'Gender-based violence', 'Legal issues', 'Other',
];
const PSYCH_SYMPTOMS = [
    'Depressed mood', 'Anxiety / excessive worry', 'Panic attacks', 'Hopelessness', 'Anhedonia (loss of pleasure)',
    'Suicidal ideation', 'Self-harm urges', 'Anger / irritability', 'Mood swings',
    'Auditory hallucinations', 'Visual hallucinations', 'Paranoid thoughts', 'Delusions',
    'Obsessive thoughts', 'Compulsive behaviours', 'Concentration difficulty', 'Memory problems',
    'Sleep disturbance', 'Nightmares / flashbacks', 'Low self-esteem', 'Dissociation',
];
const PHYS_SYMPTOMS = [
    'Headaches', 'Fatigue/Weakness', 'Sleep problems', 'Appetite disturbance', 'Weight changes',
    'Palpitations', 'Shortness of breath', 'Chest tightness', 'Sweating', 'Tremors',
    'Nausea', 'Stomach aches', 'Body aches', 'Dizziness', 'Poor self-care',
];
const SOCIAL_SYMPTOMS = [
    'Social withdrawal', 'Relationship difficulties', 'Occupational impairment',
    'Family dysfunction', 'Aggression towards others', 'Decreased daily functioning',
    'Neglect of responsibilities', 'Isolation from friends',
];
const RED_FLAGS = [
    'Active suicidal ideation', 'Suicide plan or intent', 'Previous suicide attempt',
    'Active self-harm', 'Homicidal ideation', 'Psychosis (active)', 'Substance intoxication',
    'Risk to children', 'Domestic abuse (victim)', 'Domestic abuse (perpetrator)',
    'Severe self-neglect', 'Court order / forensic',
];
const MSE_OPTIONS = {
    appearance: ['Well-groomed', 'Dishevelled', 'Inappropriate dress', 'Peculiar appearance'],
    behaviour: ['Calm & cooperative', 'Agitated', 'Restless', 'Aggressive', 'Withdrawn', 'Bizarre'],
    speech: ['Normal rate & tone', 'Pressured', 'Slow', 'Incoherent', 'Mute', 'Loud'],
    mood: ['Euthymic', 'Depressed', 'Elevated/Manic', 'Anxious', 'Irritable', 'Labile', 'Flat'],
    affect: ['Full range', 'Restricted', 'Flat', 'Blunted', 'Incongruent', 'Labile'],
    thought_form: ['Logical & coherent', 'Disorganised', 'Flight of ideas', 'Circumstantial', 'Tangential', 'Thought blocking'],
    perception: ['No abnormality', 'Auditory hallucinations', 'Visual hallucinations', 'Illusions', 'Depersonalisation'],
    cognition: ['Alert & oriented', 'Confused', 'Impaired concentration', 'Memory impairment'],
    insight: ['Full insight', 'Partial insight', 'No insight'],
    judgement: ['Intact', 'Impaired', 'Poor'],
};

const MultiCheck = ({ options, selected, onChange, cols = 2 }) => (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '6px 12px' }}>
        {options.map(o => (
            <label key={o} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                <input type="checkbox" checked={selected.includes(o)} style={{ marginTop: 2, flexShrink: 0 }}
                    onChange={e => onChange(e.target.checked ? [...selected, o] : selected.filter(s => s !== o))} />
                {o}
            </label>
        ))}
    </div>
);

export default function PsychNewCase() {
    const navigate = useNavigate();
    const lookupRef = useRef();
    const [patient, setPatient] = useState(null);
    const [saving, setSaving] = useState(false);
    const [step, setStep] = useState(1);

    const [form, setForm] = useState({
        referral_date: '', referral_uptake: '', referral_source: '', counsellor_name: '', department: 'Psychiatry',
        num_siblings_male: 0, num_siblings_female: 0, birth_order: '', family_structure: '', education_level: '',
        presenting_complaints: '',
        sources_of_distress: [], psychological_symptoms: [], physical_symptoms: [], social_symptoms: [], red_flags: [],
        mse_appearance: '', mse_behaviour: '', mse_speech: '', mse_mood: '', mse_affect: '',
        mse_thought_form: '', mse_thought_content: '', mse_perception: '', mse_cognition: '', mse_insight: '', mse_judgement: '',
        diagnosis: '', icd10_code: '', dsm5_code: '', risk_level: 'Low',
        treatment_plan: '', goals: '', follow_up_interval: '',
    });

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const inp = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' };
    const sel = { ...inp };
    const ta = { ...inp, resize: 'vertical', minHeight: 80 };
    const lbl = { fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 4, display: 'block' };
    const sectionTitle = (t, icon) => (
        <div style={{ fontSize: 14, fontWeight: 800, color: '#7c3aed', marginBottom: 14, paddingBottom: 8, borderBottom: '2px solid #ede9fe', display: 'flex', alignItems: 'center', gap: 6 }}>
            {icon} {t}
        </div>
    );
    const card = { background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', padding: 20, marginBottom: 16 };

    const submit = async () => {
        if (!patient) { toast.error('Please select a patient'); return; }
        setSaving(true);
        try {
            const res = await psychiatryAPI.createCase({ ...form, patient_id: patient.id });
            toast.success(`Case ${res.data.case_number} created!`);
            navigate(`/psych/cases/${res.data.case.id}`);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to create case');
        } finally { setSaving(false); }
    };

    const hasRedFlags = form.red_flags.length > 0;

    return (
        <div style={{ maxWidth: 900 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <button onClick={() => navigate('/psych')}
                    style={{ padding: '7px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    ← Back
                </button>
                <div>
                    <h2 style={{ fontSize: 18, fontWeight: 700, color: '#7c3aed', margin: 0 }}>🧠 New Psychiatric Case</h2>
                    <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Initial psychiatric assessment & registration</p>
                </div>
            </div>

            {hasRedFlags && (
                <div style={{ background: '#fef2f2', border: '2px solid #dc2626', borderRadius: 8, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 20 }}>⚠️</span>
                    <div>
                        <div style={{ fontWeight: 800, color: '#dc2626', fontSize: 13 }}>HIGH RISK FLAGS DETECTED</div>
                        <div style={{ fontSize: 12, color: '#7f1d1d' }}>{form.red_flags.join(' • ')}</div>
                    </div>
                </div>
            )}

            {/* Step tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', padding: 6 }}>
                {[['1', 'Patient & Referral'], ['2', 'Symptoms'], ['3', 'MSE'], ['4', 'Diagnosis & Plan']].map(([n, label]) => (
                    <button key={n} onClick={() => setStep(parseInt(n))}
                        style={{ flex: 1, padding: '8px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: step === parseInt(n) ? 700 : 500,
                            background: step === parseInt(n) ? '#7c3aed' : 'transparent',
                            color: step === parseInt(n) ? 'white' : '#64748b' }}>
                        {n}. {label}
                    </button>
                ))}
            </div>

            {/* Step 1: Patient & Referral */}
            {step === 1 && (<>
                <div style={card}>
                    {sectionTitle('Patient Selection', '👤')}
                    <PatientLookup ref={lookupRef} onPatientReady={setPatient} department="Psychiatry" />
                    {patient && (
                        <div style={{ marginTop: 12, background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: 8, padding: '10px 14px', display: 'flex', gap: 20 }}>
                            <div><div style={{ fontSize: 10, color: '#7c3aed', fontWeight: 700 }}>NAME</div><div style={{ fontSize: 13, fontWeight: 700 }}>{patient.full_name}</div></div>
                            <div><div style={{ fontSize: 10, color: '#7c3aed', fontWeight: 700 }}>MRN</div><div style={{ fontSize: 13, fontWeight: 700 }}>{patient.mrn}</div></div>
                            <div><div style={{ fontSize: 10, color: '#7c3aed', fontWeight: 700 }}>AGE</div><div style={{ fontSize: 13, fontWeight: 700 }}>{patient.age_years}y {patient.gender}</div></div>
                            <div><div style={{ fontSize: 10, color: '#7c3aed', fontWeight: 700 }}>PHONE</div><div style={{ fontSize: 13, fontWeight: 700 }}>{patient.phone || '—'}</div></div>
                        </div>
                    )}
                </div>

                <div style={card}>
                    {sectionTitle('Referral Information', '📋')}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                        <div><label style={lbl}>Referral Date</label><input style={inp} type="date" value={form.referral_date} onChange={e => set('referral_date', e.target.value)} /></div>
                        <div><label style={lbl}>Referral Uptake Date</label><input style={inp} type="date" value={form.referral_uptake} onChange={e => set('referral_uptake', e.target.value)} /></div>
                        <div><label style={lbl}>Referral Source *</label>
                            <select style={sel} value={form.referral_source} onChange={e => set('referral_source', e.target.value)}>
                                <option value="">Select...</option>
                                {REFERRAL_SOURCES.map(r => <option key={r}>{r}</option>)}
                            </select>
                        </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div><label style={lbl}>Assigned Counsellor *</label>
                            <select style={sel} value={form.counsellor_name} onChange={e => set('counsellor_name', e.target.value)}>
                                <option value="">Select counsellor...</option>
                                {COUNSELLORS.map(c => <option key={c}>{c}</option>)}
                            </select>
                        </div>
                        <div><label style={lbl}>Department</label><input style={inp} value={form.department} onChange={e => set('department', e.target.value)} /></div>
                    </div>
                </div>

                <div style={card}>
                    {sectionTitle('Family Background', '👨‍👩‍👧')}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                        <div><label style={lbl}>Siblings (Male)</label><input style={inp} type="number" min="0" value={form.num_siblings_male} onChange={e => set('num_siblings_male', e.target.value)} /></div>
                        <div><label style={lbl}>Siblings (Female)</label><input style={inp} type="number" min="0" value={form.num_siblings_female} onChange={e => set('num_siblings_female', e.target.value)} /></div>
                        <div><label style={lbl}>Birth Order</label>
                            <select style={sel} value={form.birth_order} onChange={e => set('birth_order', e.target.value)}>
                                <option value="">Select...</option>
                                {BIRTH_ORDERS.map(o => <option key={o}>{o}</option>)}
                            </select>
                        </div>
                        <div><label style={lbl}>Education Level</label>
                            <select style={sel} value={form.education_level} onChange={e => set('education_level', e.target.value)}>
                                <option value="">Select...</option>
                                {EDUCATION.map(e => <option key={e}>{e}</option>)}
                            </select>
                        </div>
                    </div>
                    <div><label style={lbl}>Family Structure</label>
                        <select style={sel} value={form.family_structure} onChange={e => set('family_structure', e.target.value)}>
                            <option value="">Select...</option>
                            {FAMILY_STRUCTURES.map(f => <option key={f}>{f}</option>)}
                        </select>
                    </div>
                </div>

                <div style={card}>
                    {sectionTitle('Presenting Complaints', '📝')}
                    <textarea style={ta} value={form.presenting_complaints} onChange={e => set('presenting_complaints', e.target.value)}
                        placeholder="Describe the patient's main complaints in their own words..." />
                </div>
            </>)}

            {/* Step 2: Symptoms */}
            {step === 2 && (<>
                <div style={card}>
                    {sectionTitle('Sources of Distress', '😟')}
                    <MultiCheck options={SOURCES_OF_DISTRESS} selected={form.sources_of_distress} onChange={v => set('sources_of_distress', v)} cols={2} />
                </div>
                <div style={card}>
                    {sectionTitle('Psychological Symptoms', '🧠')}
                    <MultiCheck options={PSYCH_SYMPTOMS} selected={form.psychological_symptoms} onChange={v => set('psychological_symptoms', v)} cols={3} />
                </div>
                <div style={card}>
                    {sectionTitle('Physical Symptoms', '💊')}
                    <MultiCheck options={PHYS_SYMPTOMS} selected={form.physical_symptoms} onChange={v => set('physical_symptoms', v)} cols={3} />
                </div>
                <div style={card}>
                    {sectionTitle('Social Symptoms', '👥')}
                    <MultiCheck options={SOCIAL_SYMPTOMS} selected={form.social_symptoms} onChange={v => set('social_symptoms', v)} cols={2} />
                </div>
                <div style={{ ...card, border: '2px solid #fca5a5', background: '#fff5f5' }}>
                    {sectionTitle('⚠️ Red Flags / Risk Indicators', '🚨')}
                    <p style={{ fontSize: 11, color: '#dc2626', marginBottom: 10, fontWeight: 600 }}>Check all that apply — these will trigger High Risk flag on the case</p>
                    <MultiCheck options={RED_FLAGS} selected={form.red_flags} onChange={v => { set('red_flags', v); if (v.length > 0) set('risk_level', 'High'); else set('risk_level', 'Low'); }} cols={2} />
                </div>
            </>)}

            {/* Step 3: Mental Status Examination */}
            {step === 3 && (
                <div style={card}>
                    {sectionTitle('Mental Status Examination (MSE)', '🔬')}
                    <p style={{ fontSize: 11, color: '#64748b', marginBottom: 16 }}>Standardised clinical observation of patient's mental state at time of assessment</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                        {[
                            ['mse_appearance', 'Appearance', 'appearance'],
                            ['mse_behaviour', 'Behaviour / Psychomotor Activity', 'behaviour'],
                            ['mse_speech', 'Speech', 'speech'],
                            ['mse_mood', 'Mood (subjective)', 'mood'],
                            ['mse_affect', 'Affect (objective)', 'affect'],
                            ['mse_thought_form', 'Thought Form', 'thought_form'],
                            ['mse_perception', 'Perception', 'perception'],
                            ['mse_cognition', 'Cognition', 'cognition'],
                            ['mse_insight', 'Insight', 'insight'],
                            ['mse_judgement', 'Judgement', 'judgement'],
                        ].map(([field, label, key]) => (
                            <div key={field}>
                                <label style={lbl}>{label}</label>
                                <select style={sel} value={form[field]} onChange={e => set(field, e.target.value)}>
                                    <option value="">Select...</option>
                                    {MSE_OPTIONS[key]?.map(o => <option key={o}>{o}</option>)}
                                </select>
                            </div>
                        ))}
                    </div>
                    <div style={{ marginTop: 14 }}>
                        <label style={lbl}>Thought Content (describe any delusions, obsessions, suicidal/homicidal ideation)</label>
                        <textarea style={ta} value={form.mse_thought_content} onChange={e => set('mse_thought_content', e.target.value)}
                            placeholder="Describe thought content findings..." />
                    </div>
                </div>
            )}

            {/* Step 4: Diagnosis & Plan */}
            {step === 4 && (<>
                <div style={card}>
                    {sectionTitle('Diagnosis', '🔬')}
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                        <div><label style={lbl}>Diagnosis *</label><input style={inp} value={form.diagnosis} onChange={e => set('diagnosis', e.target.value)} placeholder="e.g. Major Depressive Disorder" /></div>
                        <div><label style={lbl}>ICD-10 Code</label><input style={inp} value={form.icd10_code} onChange={e => set('icd10_code', e.target.value)} placeholder="e.g. F32.1" /></div>
                        <div><label style={lbl}>DSM-5 Code</label><input style={inp} value={form.dsm5_code} onChange={e => set('dsm5_code', e.target.value)} placeholder="e.g. 296.22" /></div>
                    </div>
                    <div><label style={lbl}>Risk Level</label>
                        <div style={{ display: 'flex', gap: 10 }}>
                            {['Low', 'Medium', 'High'].map(r => {
                                const rc = RISK_COLOR[r];
                                return (
                                    <button key={r} onClick={() => set('risk_level', r)}
                                        style={{ flex: 1, padding: '10px', border: `2px solid ${form.risk_level === r ? rc.color : '#e2e8f0'}`,
                                            borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13,
                                            background: form.risk_level === r ? rc.bg : 'white',
                                            color: form.risk_level === r ? rc.color : '#64748b' }}>
                                        {r} Risk
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <div style={card}>
                    {sectionTitle('Treatment Plan', '📋')}
                    <div style={{ marginBottom: 12 }}>
                        <label style={lbl}>Treatment Plan</label>
                        <textarea style={ta} value={form.treatment_plan} onChange={e => set('treatment_plan', e.target.value)}
                            placeholder="Outline the therapeutic approach and interventions planned..." />
                    </div>
                    <div style={{ marginBottom: 12 }}>
                        <label style={lbl}>Short & Long Term Goals</label>
                        <textarea style={ta} value={form.goals} onChange={e => set('goals', e.target.value)}
                            placeholder="Define measurable therapeutic goals..." />
                    </div>
                    <div><label style={lbl}>Follow Up Interval</label>
                        <select style={sel} value={form.follow_up_interval} onChange={e => set('follow_up_interval', e.target.value)}>
                            <option value="">Select...</option>
                            {['Weekly', 'Bi-weekly', 'Monthly', 'Every 2 months', 'As needed'].map(f => <option key={f}>{f}</option>)}
                        </select>
                    </div>
                </div>
            </>)}

            {/* Navigation buttons */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                <button onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1}
                    style={{ padding: '10px 24px', background: step === 1 ? '#f1f5f9' : '#e0e7ff', border: 'none', borderRadius: 8, cursor: step === 1 ? 'default' : 'pointer', fontSize: 13, fontWeight: 600, color: step === 1 ? '#9ca3af' : '#4338ca' }}>
                    ← Previous
                </button>
                {step < 4
                    ? <button onClick={() => setStep(s => s + 1)}
                        style={{ padding: '10px 24px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                        Next →
                      </button>
                    : <button onClick={submit} disabled={saving}
                        style={{ padding: '10px 28px', background: saving ? '#9ca3af' : '#059669', color: 'white', border: 'none', borderRadius: 8, cursor: saving ? 'default' : 'pointer', fontSize: 13, fontWeight: 700 }}>
                        {saving ? 'Saving...' : '✅ Register Case'}
                      </button>
                }
            </div>
        </div>
    );
}

const RISK_COLOR = { High: { bg: '#fef2f2', color: '#dc2626' }, Medium: { bg: '#fff7ed', color: '#ea580c' }, Low: { bg: '#f0fdf4', color: '#16a34a' } };
